import React, { useEffect } from 'react';
import { sentenceCase } from 'change-case';
import { Row, Col, Button, Form, Input, Checkbox, Select, Tooltip, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ToastNotification from '../../utils/ToastNotification';
import { JOB_STATUS } from '../../types/Job';
import { Client } from '../../types/Client';

interface JobListSearchFiltersInterfaceProps {
    wrapperStyleClass?: string;
    isLoadingData: boolean;
    clients: Client[];
    initialFilters: any;
    onSetSearchFilters(filters: any, opts?: { isPaging: boolean }): void;
}

const JobListSearchFilters: React.FC<JobListSearchFiltersInterfaceProps> = (
    props: JobListSearchFiltersInterfaceProps,
): React.ReactElement => {
    const { wrapperStyleClass, isLoadingData, clients, onSetSearchFilters: setFiltersToApply, initialFilters } = props;

    const [form] = Form.useForm();

    const jobStatuses = Object.keys(JOB_STATUS)
        .map((jobStatus) => ({
            value: jobStatus,
            key: jobStatus,
            text: sentenceCase(jobStatus),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const clientList = clients
        .map((client: { id: number; name: string }) => ({
            value: client.id,
            key: client.id,
            text: client.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const hasValidFilter = (filters: { [key: string]: unknown }): boolean => {
        return Object.values(filters).some((filter) => typeof filter !== 'undefined');
    };

    const removeDirtyFilters = (filters: { [key: string]: any }): { [key: string]: any } => {
        const filtersToApply = Object.assign(filters);
        Object.keys(filtersToApply).forEach(
            (key) =>
                (!filtersToApply[key] || (Array.isArray(filtersToApply[key]) && filtersToApply[key].length === 0)) &&
                delete filtersToApply[key],
        );
        return filtersToApply;
    };

    const setSearchFilters = (): void => {
        const filters = removeDirtyFilters(form.getFieldsValue());
        if (hasValidFilter(filters)) {
            setFiltersToApply({ ...filters });
            return;
        }
        setFiltersToApply({});
        ToastNotification({
            message: 'No search criteria',
            description: 'Job list will be reloaded.',
        });
    };

    const resetFilters = (): void => {
        form.resetFields();
        setFiltersToApply({});
    };

    useEffect(() => {
        form.setFieldsValue(initialFilters);
    }, [initialFilters]);

    return (
        <div className={wrapperStyleClass || 'px-4 bg-white'}>
            <Form layout="vertical" form={form} id="filter-form" name="filter-form">
                <Row gutter={24}>
                    <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }} title="Id">
                        <Form.Item label="Id" id="id" name="id">
                            <Input
                                placeholder="Job or Middleware"
                                id="id"
                                name="id"
                                title="Job or Middleware"
                                autoComplete="off"
                                onPressEnter={setSearchFilters}
                                allowClear
                                autoFocus
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }} title="Client reference">
                        <Form.Item label="Client reference" id="client_reference" name="client_reference">
                            <Input
                                placeholder="Type client reference"
                                id="client_reference"
                                name="client_reference"
                                title="Client reference"
                                autoComplete="off"
                                onPressEnter={setSearchFilters}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }} title="Clients">
                        <Form.Item label="Clients" id="client_ids" name="client_ids">
                            <Select
                                id="client_ids"
                                mode="multiple"
                                placeholder="Select clients"
                                style={{ width: '100%' }}
                                optionFilterProp="children"
                                filterOption={(input, option): boolean =>
                                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                showArrow
                            >
                                {clientList.map((item: { value: any; key: number; text: string }) => (
                                    <Select.Option key={item.key} value={`${item.value}`}>
                                        {item.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={4} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 4 }} title="Job statuses">
                        <Form.Item label="Job statuses" id="status" name="status">
                            <Select
                                id="status"
                                mode="multiple"
                                placeholder="Select job statuses"
                                style={{ width: '100%' }}
                                optionFilterProp="children"
                                filterOption={(input: string, option): boolean =>
                                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                showArrow
                            >
                                {jobStatuses.map((item: { value: string; key: string; text: string }) => (
                                    <Select.Option key={item.key} value={`${item.value}`}>
                                        {item.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col
                        span={4}
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 4 }}
                        title="Active jobs"
                        className="d-flex"
                    >
                        <Row className="d-flex align-items-center">
                            <Form.Item id="active" name="active" valuePropName="checked" className="m-0">
                                <Checkbox id="active" name="active">
                                    Active jobs
                                </Checkbox>
                            </Form.Item>
                            <Tooltip
                                className="d-inline-flex"
                                title={`Alias for job statuses = ${JOB_STATUS.AWAITING_JOB_CREATION}, ${JOB_STATUS.AWAITING_STACK_COMPLETION} or ${JOB_STATUS.AWAITING_STACKED_JOB_CREATION}. Overriding the status parameter`}
                            >
                                <InfoCircleOutlined className="text-muted" />
                            </Tooltip>
                        </Row>
                    </Col>
                    <Col
                        span={4}
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 4 }}
                        className="d-flex flex-row align-items-center justify-content-end"
                    >
                        <Space>
                            <Button
                                id="btn-search"
                                name="btn-search"
                                title="Search"
                                onClick={setSearchFilters}
                                disabled={isLoadingData}
                                className="ant-btn text-white btn-primary bg-primary"
                            >
                                Search
                            </Button>
                            <Button
                                id="btn-reset-filters"
                                name="btn-reset-filters"
                                title="Reset"
                                onClick={resetFilters}
                                disabled={isLoadingData}
                                className="ant-btn"
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default JobListSearchFilters;
